/**
 * ChainRosaryPage — passcode-gated page for managing Chain Rosary call participants.
 * Passcode: 228984
 */
import { useEffect, useRef, useState } from 'react';
import {
  Phone, Plus, Trash2, Bell, BellOff, Lock,
  Edit2, Clock, ArrowUpDown, History, CheckCircle2, XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getParticipants, addParticipant, updateParticipant, deleteParticipant,
  getCallLogs, getCurrentIST,
} from '@/services/rosaryService';
import type { RosaryParticipant, RosaryCallLog, CreateParticipantInput } from '@/services/rosaryService';
import { cn } from '@/utils/cn';

// ── Country codes ──────────────────────────────────────────────────────────────

const COUNTRY_CODES = [
  { code: '+91',  label: '🇮🇳 India (+91)' },
  { code: '+1',   label: '🇺🇸 USA/Canada (+1)' },
  { code: '+44',  label: '🇬🇧 UK (+44)' },
  { code: '+61',  label: '🇦🇺 Australia (+61)' },
  { code: '+971', label: '🇦🇪 UAE (+971)' },
  { code: '+974', label: '🇶🇦 Qatar (+974)' },
  { code: '+966', label: '🇸🇦 Saudi Arabia (+966)' },
  { code: '+965', label: '🇰🇼 Kuwait (+965)' },
  { code: '+968', label: '🇴🇲 Oman (+968)' },
  { code: '+973', label: '🇧🇭 Bahrain (+973)' },
  { code: '+60',  label: '🇲🇾 Malaysia (+60)' },
  { code: '+65',  label: '🇸🇬 Singapore (+65)' },
  { code: '+49',  label: '🇩🇪 Germany (+49)' },
  { code: '+33',  label: '🇫🇷 France (+33)' },
  { code: '+39',  label: '🇮🇹 Italy (+39)' },
  { code: '+34',  label: '🇪🇸 Spain (+34)' },
  { code: '+31',  label: '🇳🇱 Netherlands (+31)' },
  { code: '+55',  label: '🇧🇷 Brazil (+55)' },
  { code: '+27',  label: '🇿🇦 South Africa (+27)' },
  { code: '+64',  label: '🇳🇿 New Zealand (+64)' },
  { code: '+81',  label: '🇯🇵 Japan (+81)' },
  { code: '+82',  label: '🇰🇷 South Korea (+82)' },
  { code: '+852', label: '🇭🇰 Hong Kong (+852)' },
  { code: '+886', label: '🇹🇼 Taiwan (+886)' },
  { code: '+94',  label: '🇱🇰 Sri Lanka (+94)' },
  { code: '+880', label: '🇧🇩 Bangladesh (+880)' },
  { code: '+92',  label: '🇵🇰 Pakistan (+92)' },
  { code: '+977', label: '🇳🇵 Nepal (+977)' },
];

// ── Passcode gate ──────────────────────────────────────────────────────────────

const CORRECT_PASSCODE = '228984';
const SESSION_KEY      = 'rosary_unlocked';

function PasscodeGate({ onUnlock }: { onUnlock: () => void }) {
  const [code,  setCode]  = useState('');
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code === CORRECT_PASSCODE) {
      sessionStorage.setItem(SESSION_KEY, '1');
      onUnlock();
    } else {
      setError(true);
      setCode('');
      setTimeout(() => setError(false), 1500);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[70vh]">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-8 w-full max-w-sm text-center">
        <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
          <Lock className="h-6 w-6 text-rose-500" />
        </div>
        <h2 className="text-lg font-bold text-gray-900 mb-1">Chain Rosary</h2>
        <p className="text-sm text-gray-500 mb-6">Enter passcode to continue</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            ref={inputRef}
            type="password"
            inputMode="numeric"
            maxLength={10}
            value={code}
            onChange={e => setCode(e.target.value)}
            placeholder="Passcode"
            className={cn(
              'w-full text-center text-xl font-bold tracking-[0.4em] px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all',
              error
                ? 'border-red-400 ring-2 ring-red-200 bg-red-50 text-red-600'
                : 'border-gray-200 focus:ring-indigo-400 text-gray-800'
            )}
          />
          {error && <p className="text-sm text-red-500 font-medium">Incorrect passcode</p>}
          <button
            type="submit"
            className="w-full py-3 rounded-xl bg-rose-600 text-white font-semibold text-sm
                       hover:bg-rose-700 active:scale-95 transition-all"
          >
            Unlock
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Add / Edit form ────────────────────────────────────────────────────────────

const EMPTY_FORM: CreateParticipantInput = {
  name:                '',
  countryCode:         '+91',
  phone:               '',
  timeIST:             '06:00',
  callMessage:         '',
  notificationEnabled: true,
};

function ParticipantForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial: CreateParticipantInput;
  onSave:  (v: CreateParticipantInput) => Promise<void>;
  onCancel: () => void;
  saving:  boolean;
}) {
  const [form, setForm] = useState(initial);
  const set = (k: keyof CreateParticipantInput, v: string | boolean) =>
    setForm(p => ({ ...p, [k]: v }));

  const inputCls = 'w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-rose-400';

  return (
    <div className="space-y-4 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <p className="text-sm font-bold text-gray-900">
        {initial.name ? 'Edit Participant' : 'Add Participant'}
      </p>

      {/* Name */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label>
        <input
          value={form.name}
          onChange={e => set('name', e.target.value)}
          placeholder="e.g. Abin Mathew"
          className={inputCls}
        />
      </div>

      {/* Phone */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Mobile Number</label>
        <div className="flex gap-2">
          <select
            value={form.countryCode}
            onChange={e => set('countryCode', e.target.value)}
            className="px-2 py-2.5 rounded-xl border border-gray-200 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-rose-400 min-w-[180px]"
          >
            {COUNTRY_CODES.map(c => (
              <option key={c.code + c.label} value={c.code}>{c.label}</option>
            ))}
          </select>
          <input
            type="tel"
            value={form.phone}
            onChange={e => set('phone', e.target.value.replace(/\D/g, ''))}
            placeholder="9876543210"
            className={inputCls}
          />
        </div>
        <p className="text-[10px] text-gray-400 mt-1">Enter number without country code or leading zeros</p>
      </div>

      {/* Call Message */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Call Message</label>
        <textarea
          rows={3}
          value={form.callMessage}
          onChange={e => set('callMessage', e.target.value)}
          placeholder="e.g. Hello Abin, this is a reminder call from Angels Army for your chain rosary"
          className={inputCls}
        />
        <p className="text-[10px] text-gray-400 mt-1">This exact message will be spoken during the reminder call. Leave blank to use the default message.</p>
      </div>

      {/* Time IST */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Call Time (IST — Indian Standard Time)
        </label>
        <input
          type="time"
          value={form.timeIST}
          onChange={e => set('timeIST', e.target.value)}
          className={inputCls}
        />
        <p className="text-[10px] text-gray-400 mt-1">
          Reminder call will be made 2 minutes before this time
        </p>
      </div>

      {/* Notification */}
      <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
        <div>
          <p className="text-sm font-medium text-gray-800">Notification Required</p>
          <p className="text-xs text-gray-500">Send reminder call before their slot</p>
        </div>
        <button
          type="button"
          onClick={() => set('notificationEnabled', !form.notificationEnabled)}
          className={cn(
            'relative w-11 h-6 rounded-full transition-colors',
            form.notificationEnabled ? 'bg-rose-500' : 'bg-gray-300'
          )}
        >
          <span className={cn(
            'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
            form.notificationEnabled && 'translate-x-5'
          )} />
        </button>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={onCancel}
          className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={() => onSave(form)}
          disabled={saving || !form.name.trim() || !form.phone.trim()}
          className="flex-1 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-semibold
                     hover:bg-rose-700 active:scale-95 transition-all disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function ChainRosaryPage() {
  const [unlocked,     setUnlocked]     = useState(false);
  const [participants, setParticipants] = useState<RosaryParticipant[]>([]);
  const [loading,      setLoading]      = useState(false);
  const [showForm,     setShowForm]     = useState(false);
  const [editTarget,   setEditTarget]   = useState<RosaryParticipant | null>(null);
  const [saving,       setSaving]       = useState(false);
  const [currentIST,   setCurrentIST]   = useState(getCurrentIST());
  const [sortBy,       setSortBy]       = useState<'name' | 'time'>('time');
  const [callLogs,     setCallLogs]     = useState<RosaryCallLog[]>([]);
  const [logsLoading,  setLogsLoading]  = useState(false);
  const [showLogs,     setShowLogs]     = useState(false);

  // Check session unlock
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY) === '1') setUnlocked(true);
  }, []);

  // Clock tick
  useEffect(() => {
    const t = setInterval(() => setCurrentIST(getCurrentIST()), 15_000);
    return () => clearInterval(t);
  }, []);

  // Load participants once unlocked
  useEffect(() => {
    if (!unlocked) return;
    setLoading(true);
    getParticipants().then(p => { setParticipants(p); setLoading(false); });
  }, [unlocked]);

  // Load logs when panel opens
  useEffect(() => {
    if (!showLogs) return;
    setLogsLoading(true);
    getCallLogs(100).then(logs => { setCallLogs(logs); setLogsLoading(false); });
  }, [showLogs]);

  // Sorted participants
  const sorted = [...participants].sort((a, b) =>
    sortBy === 'name'
      ? a.name.localeCompare(b.name)
      : a.timeIST.localeCompare(b.timeIST)
  );

  if (!unlocked) {
    return <PasscodeGate onUnlock={() => setUnlocked(true)} />;
  }

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleAdd = async (form: CreateParticipantInput) => {
    setSaving(true);
    try {
      const id = await addParticipant(form);
      setParticipants(prev => [...prev, {
        ...form, id,
        createdAt: new Date(),
      }]);
      setShowForm(false);
      toast.success('Participant added');
    } catch {
      toast.error('Failed to add participant');
    }
    setSaving(false);
  };

  const handleEdit = async (form: CreateParticipantInput) => {
    if (!editTarget) return;
    setSaving(true);
    try {
      await updateParticipant(editTarget.id, form);
      setParticipants(prev => prev.map(p =>
        p.id === editTarget.id ? { ...p, ...form } : p
      ));
      setEditTarget(null);
      toast.success('Updated');
    } catch {
      toast.error('Failed to update');
    }
    setSaving(false);
  };

  const handleToggleNotification = async (p: RosaryParticipant) => {
    const next = !p.notificationEnabled;
    await updateParticipant(p.id, { notificationEnabled: next });
    setParticipants(prev => prev.map(x => x.id === p.id ? { ...x, notificationEnabled: next } : x));
    toast.success(next ? 'Notifications enabled' : 'Notifications disabled');
  };

  const handleDelete = async (p: RosaryParticipant) => {
    if (!confirm(`Remove ${p.name} from Chain Rosary?`)) return;
    await deleteParticipant(p.id);
    setParticipants(prev => prev.filter(x => x.id !== p.id));
    toast.success('Removed');
  };

  // Format 24h → 12h display
  const fmt12 = (hhmm: string) => {
    const [hStr, mStr] = hhmm.split(':');
    const h = parseInt(hStr, 10);
    const m = mStr;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  };

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">⛪ Chain Rosary</h1>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Clock className="h-3.5 w-3.5 text-gray-400" />
            <span className="text-xs text-gray-500">Current IST: <span className="font-semibold text-gray-700">{fmt12(currentIST)}</span></span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowLogs(p => !p); }}
            className={cn(
              'flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold active:scale-95 transition-all border',
              showLogs ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            )}
          >
            <History className="h-4 w-4" /> Logs
          </button>
          <button
            onClick={() => { setShowForm(true); setEditTarget(null); }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-semibold active:scale-95 transition-all"
          >
            <Plus className="h-4 w-4" /> Add Person
          </button>
        </div>
      </div>

      {/* Sort controls */}
      <div className="flex items-center gap-2">
        <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />
        <span className="text-xs text-gray-500">Sort by:</span>
        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
          {(['time', 'name'] as const).map(s => (
            <button key={s} onClick={() => setSortBy(s)}
              className={cn('px-3 py-1 rounded-lg text-xs font-semibold transition-all capitalize',
                sortBy === s ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500')}>
              {s === 'time' ? 'Time' : 'Name (A–Z)'}
            </button>
          ))}
        </div>
      </div>

      {/* Notice */}
      <div className="bg-rose-50 border border-rose-100 rounded-xl p-3 text-xs text-rose-700">
        <p className="font-semibold mb-0.5">How it works</p>
        <p>Each person will receive an automated reminder call <strong>2 minutes before</strong> their assigned time (IST). The call will say: <em>"Angels Army reminder call for Chain Rosary"</em></p>
      </div>

      {/* Add form */}
      {showForm && !editTarget && (
        <ParticipantForm
          initial={EMPTY_FORM}
          onSave={handleAdd}
          onCancel={() => setShowForm(false)}
          saving={saving}
        />
      )}

      {/* Call Log panel */}
      {showLogs && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <History className="h-4 w-4 text-indigo-500" />
            <p className="text-sm font-bold text-gray-900">Call Log</p>
            <span className="ml-auto text-xs text-gray-400">{callLogs.length} entries</span>
          </div>
          {logsLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => <div key={i} className="h-12 rounded-xl bg-gray-100 animate-pulse" />)}
            </div>
          ) : callLogs.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-6">No calls logged yet.</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {callLogs.map(log => (
                <div key={log.id} className={cn(
                  'flex items-start gap-3 p-3 rounded-xl border text-xs',
                  log.status === 'success' ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'
                )}>
                  {log.status === 'success'
                    ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                    : <XCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                  }
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-gray-800">{log.participantName}</span>
                      <span className="text-gray-400 shrink-0">
                        {log.triggeredAt
                          ? log.triggeredAt.toLocaleString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })
                          : '—'}
                      </span>
                    </div>
                    <p className="text-gray-500 truncate">{log.phone} · Slot: {fmt12(log.scheduledTimeIST)}</p>
                    {log.status === 'success' && log.twilioCallSid && (
                      <p className="text-emerald-600 font-mono text-[10px] truncate">SID: {log.twilioCallSid}</p>
                    )}
                    {log.status === 'failed' && log.error && (
                      <p className="text-red-500 truncate">{log.error}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 rounded-2xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 text-gray-400 text-sm">
          <Phone className="h-10 w-10 mx-auto mb-3 text-gray-200" />
          <p>No participants yet. Add the first person.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map(p => (
            <div key={p.id}>
              {editTarget?.id === p.id ? (
                <ParticipantForm
                  initial={{ name: p.name, countryCode: p.countryCode, phone: p.phone, timeIST: p.timeIST, callMessage: p.callMessage, notificationEnabled: p.notificationEnabled }}
                  onSave={handleEdit}
                  onCancel={() => setEditTarget(null)}
                  saving={saving}
                />
              ) : (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center shrink-0">
                      <span className="text-sm font-bold text-rose-600">{p.name.charAt(0).toUpperCase()}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 justify-between">
                        <p className="text-sm font-bold text-gray-900 truncate">{p.name}</p>
                        <div className="flex items-center gap-1.5">
                          {/* Notification toggle */}
                          <button
                            onClick={() => handleToggleNotification(p)}
                            title={p.notificationEnabled ? 'Disable notification' : 'Enable notification'}
                            className={cn(
                              'p-1.5 rounded-lg transition-colors',
                              p.notificationEnabled
                                ? 'bg-rose-50 text-rose-500 hover:bg-rose-100'
                                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                            )}
                          >
                            {p.notificationEnabled ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
                          </button>
                          {/* Edit */}
                          <button
                            onClick={() => { setEditTarget(p); setShowForm(false); }}
                            className="p-1.5 rounded-lg bg-gray-100 text-gray-500 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          {/* Delete */}
                          <button
                            onClick={() => handleDelete(p)}
                            className="p-1.5 rounded-lg bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        {/* Phone */}
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Phone className="h-3 w-3" />
                          {p.countryCode} {p.phone}
                        </span>
                        {/* Time */}
                        <span className="flex items-center gap-1 text-xs font-semibold text-gray-700">
                          <Clock className="h-3 w-3 text-rose-400" />
                          {fmt12(p.timeIST)} IST
                        </span>
                        {/* Status */}
                        <span className={cn(
                          'text-[10px] font-bold px-2 py-0.5 rounded-full',
                          p.notificationEnabled
                            ? 'bg-emerald-50 text-emerald-600'
                            : 'bg-gray-100 text-gray-400'
                        )}>
                          {p.notificationEnabled ? 'Call ON' : 'Call OFF'}
                        </span>
                      </div>
                      {/* Call message preview */}
                      {p.callMessage && (
                        <p className="mt-1.5 text-[11px] text-gray-400 italic truncate">
                          "{p.callMessage}"
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
